export type ResultBoardDiagramType = "M" | "V" | "N" | "D";
type ResultBoardGraphPointsMode = "off" | "max" | "all";

interface ResultBoardValue {
  horizontalValue: number;
  verticalValue: number;
}

interface ResultBoardValueLabel extends ResultBoardValue {
  text: string;
}

export const RESULT_BOARD_LANE_COUNT = 3;
export const resultBoardWidth = (laneWidth: number): number => laneWidth * RESULT_BOARD_LANE_COUNT;

// Moment/shear share one lane, deformation uses one lane, and normal force uses one lane.
export const resultBoardLaneIndex = (type: ResultBoardDiagramType): number => {
  if (type === "N") return 0;
  if (type === "M" || type === "V") return 1;
  return 2;
};

export const resultBoardLoadBase = (sectionHalfExtent: number, boardGap: number): number =>
  sectionHalfExtent + boardGap;

export type ResultBoardScreenSide = "left" | "right";

// Which side of the screen a result board ends up on. Both 3D views put +y to the right and +z to
// the left, and "right hand" swaps them (see isoProject), so the xy board is on the right unless
// rightHand is set.
export const resultBoardScreenSide = (direction: "xy" | "xz", rightHand: boolean): ResultBoardScreenSide =>
  (direction === "xy") !== rightHand ? "right" : "left";

// The offset indicator of an eccentric vertical load: a path in the section plane from the centre
// (0, 0) to (y, z), at most two straight legs. The leg along the axis with the larger |offset| comes
// first, the other second; a leg of zero length is left out, and no offset gives no path at all.
export const eccentricityOffsetPath = (y: number, z: number, tolerance = 1e-6): Array<{ y: number; z: number }> => {
  const hasY = Math.abs(y) > tolerance;
  const hasZ = Math.abs(z) > tolerance;
  if (!hasY && !hasZ) return [];
  const start = { y: 0, z: 0 };
  const end = { y, z };
  if (!hasY || !hasZ) return [start, end];
  const corner = Math.abs(y) >= Math.abs(z) ? { y, z: 0 } : { y: 0, z };
  return [start, corner, end];
};

// Transverse loads are drawn in one lane per load case, like the 2D view: each lane starts past the
// longest arrow of the previous one plus laneGap. Offsets are from the load board's near edge.
export const loadCaseLanes = <T extends { lcaseGuid?: string | null }>(
  loads: T[],
  arrowLength: (load: T) => number,
  laneGap: number,
): { offsetOf: (load: T) => number; outer: number } => {
  const lanes = new Map<string, T[]>();
  for (const load of loads) {
    const key = load.lcaseGuid ?? "";
    lanes.set(key, [...(lanes.get(key) ?? []), load]);
  }
  const offsets = new Map<T, number>();
  let offset = 0;
  let outer = 0;
  for (const laneLoads of lanes.values()) {
    const width = Math.max(0, ...laneLoads.map(arrowLength));
    laneLoads.forEach((load) => offsets.set(load, offset));
    outer = offset + width;
    offset = outer + laneGap;
  }
  return { offsetOf: (load) => offsets.get(load) ?? 0, outer };
};

const displaySign = (type: ResultBoardDiagramType): 1 | -1 => (type === "V" || type === "D" ? -1 : 1);

const formatResultValue = (type: ResultBoardDiagramType, value: number): string => {
  const text = (value * displaySign(type)).toFixed(2);
  return text === "-0.00" ? "0.00" : text;
};

export const resultBoardValueLabels = (
  values: ResultBoardValue[],
  type: ResultBoardDiagramType,
  mode: ResultBoardGraphPointsMode,
): ResultBoardValueLabel[] => {
  if (mode === "off" || values.length === 0) return [];

  const selected =
    mode === "all"
      ? values
      : (() => {
          const max = values.reduce((best, value) => (value.verticalValue > best.verticalValue ? value : best));
          const min = values.reduce((best, value) => (value.verticalValue < best.verticalValue ? value : best));
          return max === min ? [max] : [max, min];
        })();

  return selected
    .map((value) => ({ ...value, text: formatResultValue(type, value.verticalValue) }))
    .filter(({ text }) => text !== "0.00");
};
